import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import uniData from "./universitydata.json";

// ──────────────────────────────────────────────
// YOUR LOCAL UNIVERSITY DATA (paste your full JSON here)
// ──────────────────────────────────────────────
const ALL_UNIVERSITIES = uniData;
// ──────────────────────────────────────────────
// DERIVE AVAILABLE COUNTRIES FROM THE JSON
// ──────────────────────────────────────────────
const PRIORITY_COUNTRIES = [
  "United States",
  "United Kingdom",
  "Canada",
  "Australia",
  "Germany",
  "Ireland",
  "New Zealand",
  "France",
  "Poland",
  "Hungary",
  "Lithuania",
  "Cyprus",
];

const BUDGET_FRIENDLY = ["Poland", "Hungary", "Lithuania", "Cyprus"];
const PR_COUNTRIES = ["Canada", "Australia", "New Zealand", "Ireland"];

function buildCountryGroups(data) {
  const allCountries = [...new Set(data.map((u) => u.country))].sort();

  const grouped = {
    "Most Popular Destinations": [],
    "Lower-Cost Options": [],
    "PR-Oriented Destinations": [],
    "Other Destinations": [],
  };

  const popularSet = new Set([
    "United States",
    "United Kingdom",
    "Canada",
    "Australia",
  ]);
  const budgetSet = new Set(["Poland", "Hungary", "Lithuania", "Cyprus"]);
  const prSet = new Set(["Germany", "Ireland", "New Zealand", "France"]);

  allCountries.forEach((c) => {
    if (popularSet.has(c)) grouped["Most Popular Destinations"].push(c);
    else if (budgetSet.has(c)) grouped["Lower-Cost Options"].push(c);
    else if (prSet.has(c)) grouped["PR-Oriented Destinations"].push(c);
    else grouped["Other Destinations"].push(c);
  });

  // Remove empty groups
  Object.keys(grouped).forEach((k) => {
    if (!grouped[k].length) delete grouped[k];
  });
  return grouped;
}

const COUNTRY_GROUPS = buildCountryGroups(ALL_UNIVERSITIES);

// ──────────────────────────────────────────────
const UniversitiesPage = () => {
  const [searchParams] = useSearchParams();
  const [selectedCountry, setSelectedCountry] = useState(
    searchParams.get("country") || "",
  );
  const [universities, setUniversities] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [intent, setIntent] = useState("explore");
  const [profile, setProfile] = useState({ score: "", budget: "", level: "" });
  const [visibleCount, setVisibleCount] = useState(10);
  const [shortlisted, setShortlisted] = useState(new Set());

  // ── INTENT OPTIONS ──
  const intentOptions = [
    { key: "budget", label: "Lower cost" },
    { key: "pr", label: "Post-study work focus" },
    { key: "prestige", label: "Top universities" },
    { key: "fast", label: "Easier entry" },
    { key: "explore", label: "Just exploring" },
  ];

  // ── FILTER FROM LOCAL JSON when country changes ──
  useEffect(() => {
    if (!selectedCountry) {
      setUniversities([]);
      return;
    }
    const filtered = ALL_UNIVERSITIES.filter(
      (u) =>
        u.country && u.country.toLowerCase() === selectedCountry.toLowerCase(),
    );
    setUniversities(filtered);
    setVisibleCount(10);
    setSearchTerm("");
  }, [selectedCountry]);

  // ── LOAD SHORTLIST from localStorage ──
  useEffect(() => {
    try {
      const saved = localStorage.getItem("university_shortlist");
      if (saved) setShortlisted(new Set(JSON.parse(saved)));
    } catch {}
  }, []);

  const saveShortlist = useCallback((set) => {
    try {
      localStorage.setItem("university_shortlist", JSON.stringify([...set]));
    } catch {}
  }, []);

  // ── INSIGHTS ENGINE ──
  const getUniversityInsights = useCallback(
    (uni, index) => {
      const name = (uni?.name || "").toLowerCase();

      const isEliteSignal =
        name.includes("state") ||
        name.includes("national") ||
        name.includes("university of") ||
        name.includes("university college");
      const isTechSignal =
        name.includes("technology") ||
        name.includes("engineering") ||
        name.includes("polytechnic") ||
        name.includes("technical");
      const isBusinessSignal =
        name.includes("business") || name.includes("management");
      const isRegionalSignal =
        name.includes("college") ||
        name.includes("regional") ||
        name.includes("community") ||
        name.includes("state college");
      const isResearchHeavy =
        name.includes("university") && !name.includes("college");

      const countrySpark = {
        "United States": "prestige, research focus",
        "United Kingdom": "fast-track masters, London network",
        Canada: "PR pathways, work permits",
        Australia: "research strength, post-study work",
        Germany: "low tuition, engineering programs",
        Ireland: "EU access, English instruction",
        "New Zealand": "safety, quality of life",
        France: "elite Grandes Écoles",
        Poland: "affordable EU degrees",
        Hungary: "scholarships, central Europe",
        Lithuania: "low-cost Baltic entry",
        Cyprus: "English programs, Mediterranean location",
      };
      const spark = countrySpark[selectedCountry] || "international study";

      const flags = {
        budgetFriendly: BUDGET_FRIENDLY.includes(selectedCountry),
        prestige: isEliteSignal,
        prPathway: PR_COUNTRIES.includes(selectedCountry),
        easierEntry: isRegionalSignal,
        researchHeavy: isResearchHeavy,
        bachelorFocused: isRegionalSignal || name.includes("college"),
      };

      let whyThisOne, whoLovesIt, watchOut;
      if (isEliteSignal) {
        whyThisOne = `Known for ${spark} reputation.`;
        whoLovesIt = "Confident profiles wanting brand value.";
        watchOut = "High competition. Needs strong scores.";
      } else if (isTechSignal) {
        whyThisOne = `Technical focus, ${spark}.`;
        whoLovesIt = "STEM students with clear goals.";
        watchOut = "Narrow programs. Check majors.";
      } else if (isBusinessSignal) {
        whyThisOne = `Business programs, ${spark}.`;
        whoLovesIt = "Career switchers building networks.";
        watchOut = "ROI varies by accreditation.";
      } else if (isRegionalSignal) {
        whyThisOne = `Accessible entry, ${spark}.`;
        whoLovesIt = "Practical students prioritizing odds.";
        watchOut = "Regional job markets.";
      } else {
        whyThisOne = `${spark.charAt(0).toUpperCase() + spark.slice(1)}.`;
        whoLovesIt = "Open-minded students testing fit.";
        watchOut = "Research specific programs.";
      }

      return {
        hasWebsite: !!uni?.web_pages?.[0],
        whyThisOne,
        whoLovesIt,
        watchOut,
        isEliteSignal,
        isTechSignal,
        isRegionalSignal,
        flags,
        originalIndex: index,
      };
    },
    [selectedCountry],
  );

  // ── ENRICH ──
  const enrichedUniversities = useMemo(
    () =>
      universities.map((uni, i) => ({
        ...uni,
        insights: getUniversityInsights(uni, i),
      })),
    [universities, getUniversityInsights],
  );

  // ── SEARCH FILTER ──
  const filtered = useMemo(
    () =>
      enrichedUniversities.filter((u) =>
        (u.name || "").toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [enrichedUniversities, searchTerm],
  );

  // ── WEIGHTED SORT ──
  const intentSorted = useMemo(() => {
    const list = filtered.map((uni) => {
      let score = 0;
      const ins = uni.insights;

      if (intent === "budget" && ins.flags.budgetFriendly) score += 2;
      if (intent === "prestige" && ins.flags.prestige) score += 2;
      if (intent === "pr" && ins.flags.prPathway) score += 2;
      if (intent === "fast" && ins.flags.easierEntry) score += 2;

      if (profile.budget === "Under ₹15L")
        score += ins.flags.budgetFriendly ? 3 : -2;
      if (profile.score === "Below 6.5") score -= ins.flags.prestige ? 3 : 0;
      if (profile.level === "Master") {
        score += ins.flags.researchHeavy
          ? 3
          : ins.flags.bachelorFocused
            ? -2
            : 0;
      }
      if (profile.level === "Bachelor") {
        score += ins.flags.bachelorFocused
          ? 3
          : ins.flags.researchHeavy
            ? -2
            : 0;
      }

      return { ...uni, sortScore: score, _origIdx: ins.originalIndex };
    });

    return list.sort(
      (a, b) => b.sortScore - a.sortScore || a._origIdx - b._origIdx,
    );
  }, [filtered, intent, profile]);

  // ── HELPERS ──
  const getProfileMismatch = (insights) => {
    if (profile.budget === "Under ₹15L" && !insights.flags.budgetFriendly)
      return "Likely above budget";
    if (profile.score === "Below 6.5" && insights.flags.prestige)
      return "Competitive entry";
    if (profile.level === "Master" && insights.flags.bachelorFocused)
      return "Undergrad focus";
    return null;
  };

  const getFitConfidence = useCallback(
    (insights) => {
      let matches = 0;
      if (intent === "budget" && insights.flags.budgetFriendly) matches++;
      if (intent === "prestige" && insights.flags.prestige) matches++;
      if (intent === "pr" && insights.flags.prPathway) matches++;
      if (intent === "fast" && insights.flags.easierEntry) matches++;
      if (getProfileMismatch(insights)) return "Low";
      return matches >= 2 ? "High" : "Medium";
    },
    [intent, profile],
  );

  const toggleShortlist = useCallback(
    (name) => {
      const next = new Set(shortlisted);
      next.has(name) ? next.delete(name) : next.add(name);
      setShortlisted(next);
      saveShortlist(next);
    },
    [shortlisted, saveShortlist],
  );

  const hasProfile = !!(profile.budget || profile.score || profile.level);
  const activeSorting = hasProfile
    ? profile.budget || profile.score || profile.level
    : intentOptions.find((i) => i.key === intent)?.label;

  const visibleUnis = intentSorted.slice(0, visibleCount);

  // ──────────────────────────────────────────────
  // RENDER
  // ──────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50">
      {/* HERO */}
      <div className="max-w-4xl mx-auto pt-16 pb-12 px-4 text-center">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 max-w-2xl mx-auto leading-tight">
          Universities students actually shortlist for overseas study
        </h1>
        <p className="text-lg text-gray-600 max-w-xl mx-auto mb-2 leading-relaxed">
          Save what interests you. Counselors match to your IELTS/GPA/profile.
        </p>
        <p className="text-sm text-gray-500 max-w-xl mx-auto">
          Based on real applications reviewed by counselors.
        </p>
      </div>

      {/* COUNTRY SELECT + SEARCH */}
      <div className="max-w-4xl mx-auto mb-16 px-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <select
            value={selectedCountry}
            onChange={(e) => {
              setSelectedCountry(e.target.value);
              setSearchTerm("");
              setVisibleCount(10);
            }}
            className="flex-1 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-sm font-medium bg-white"
          >
            <option value="">Select country</option>
            {Object.entries(COUNTRY_GROUPS).map(([group, countries]) => (
              <optgroup key={group} label={group}>
                {countries.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          <input
            type="text"
            placeholder="Search universities..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="flex-1 p-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* INTENT PILLS */}
        {/* <div className="flex flex-wrap gap-2 mt-4">
          {intentOptions.map((i) => (
            <button
              key={i.key}
              onClick={() => setIntent(i.key)}
              disabled={hasProfile}
              title={hasProfile ? "Profile sorting active" : ""}
              className={`px-4 py-2 rounded-full text-sm border transition-all ${
                intent === i.key && !hasProfile
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                  : "bg-white border-gray-300 text-gray-600 hover:border-emerald-400 hover:shadow-sm"
              } ${hasProfile ? "opacity-50" : ""}`}
            >
              {i.label}
            </button>
          ))}
        </div> */}
      </div>

      {/* MICRO PROFILE */}
      {/* <div className="max-w-4xl mx-auto mb-16 px-4">
        <div
          className={`bg-emerald-50 border-2 border-emerald-200 p-6 rounded-2xl ${hasProfile ? "ring-2 ring-emerald-300 ring-opacity-50" : ""}`}
        >
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-sm font-semibold text-emerald-900">
                Your profile shapes results
              </p>
              <p className="text-xs text-emerald-700 mt-1">
                {hasProfile
                  ? `Prioritizing matches for your ${activeSorting.toLowerCase()}`
                  : "Select to reorder results (no universities hidden)"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {hasProfile && (
                <button
                  onClick={() =>
                    setProfile({ score: "", budget: "", level: "" })
                  }
                  className="text-xs text-emerald-700 hover:text-emerald-900 font-medium underline"
                >
                  Reset
                </button>
              )}
              <span
                className={`px-2 py-1 text-xs rounded-full font-medium ${
                  hasProfile
                    ? "bg-emerald-200 text-emerald-800"
                    : "bg-gray-200 text-gray-700"
                }`}
              >
                {hasProfile ? "Active" : "Optional"}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <select
              value={profile.score}
              onChange={(e) =>
                setProfile((p) => ({ ...p, score: e.target.value }))
              }
              className="p-3 border border-emerald-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              <option value="">English score</option>
              <option>Below 6.5</option>
              <option>6.5 – 7</option>
              <option>7+</option>
            </select>
            <select
              value={profile.budget}
              onChange={(e) =>
                setProfile((p) => ({ ...p, budget: e.target.value }))
              }
              className="p-3 border border-emerald-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              <option value="">Budget range</option>
              <option>Under ₹15L</option>
              <option>₹15–25L</option>
              <option>Flexible</option>
            </select>
            <select
              value={profile.level}
              onChange={(e) =>
                setProfile((p) => ({ ...p, level: e.target.value }))
              }
              className="p-3 border border-emerald-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 bg-white"
            >
              <option value="">Study level</option>
              <option>Bachelor</option>
              <option>Master</option>
            </select>
          </div>
        </div>
      </div> */}

      {/* ── EMPTY / NO COUNTRY SELECTED ── */}
      {!selectedCountry && (
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="bg-white border border-gray-200 p-10 rounded-2xl max-w-2xl mx-auto">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Pick a country to get started
            </h3>
            <p className="text-gray-500 mb-6">
              We have universities across{" "}
              {Object.values(COUNTRY_GROUPS).flat().length} countries in our
              database.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.values(COUNTRY_GROUPS)
                .flat()
                .slice(0, 6)
                .map((c) => (
                  <button
                    key={c}
                    onClick={() => setSelectedCountry(c)}
                    className="px-4 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-sm font-medium hover:bg-emerald-100 transition-colors"
                  >
                    {c}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {/* ── NO RESULTS AFTER SEARCH ── */}
      {selectedCountry && universities.length === 0 && (
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-amber-50 border border-amber-200 p-8 rounded-2xl max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-amber-900 mb-3">
              No universities for {selectedCountry}
            </h3>
            <p className="text-amber-700 mb-5 text-sm">
              No data exists for this country in the current dataset. Try
              another country.
            </p>
            <div className="flex flex-wrap gap-2 justify-center">
              {Object.values(COUNTRY_GROUPS)
                .flat()
                .filter((c) => c !== selectedCountry)
                .slice(0, 4)
                .map((c) => (
                  <button
                    key={c}
                    onClick={() => setSelectedCountry(c)}
                    className="px-5 py-2 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700"
                  >
                    {c}
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}

      {selectedCountry && universities.length > 0 && filtered.length === 0 && (
        <div className="max-w-4xl mx-auto px-4 py-16 text-center">
          <div className="bg-gray-50 border border-gray-200 p-8 rounded-2xl max-w-2xl mx-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              No matches found
            </h3>
            <p className="text-gray-600 mb-5">
              No universities match "
              <span className="font-medium">{searchTerm}</span>" in{" "}
              {selectedCountry}.
            </p>
            <button
              onClick={() => setSearchTerm("")}
              className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl text-sm font-medium hover:bg-emerald-700"
            >
              Clear search
            </button>
          </div>
        </div>
      )}

      {/* ── RESULTS GRID ── */}
      {selectedCountry && filtered.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 pb-20">
          {/* Results header */}
          <div className="mb-12">
            <p className="text-2xl font-semibold text-gray-900">
              {filtered.length} universit{filtered.length !== 1 ? "ies" : "y"}{" "}
              in {selectedCountry}
            </p>
            
            {shortlisted.size > 0 && (
              <p className="text-sm text-emerald-600 font-medium mt-2">
                {shortlisted.size} saved
              </p>
            )}
          </div>

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visibleUnis.map((uni, idx) => {
              const ins = uni.insights;
              const isSaved = shortlisted.has(uni.name || "");
              const mismatch = getProfileMismatch(ins);
              const confidence = getFitConfidence(ins);

              const variant = ins.isEliteSignal ? 1 : ins.isTechSignal ? 0 : 2;
              const emphasis =
                variant === 0
                  ? ins.whoLovesIt
                  : variant === 1
                    ? ins.whyThisOne
                    : ins.watchOut;
              const variantStyles = [
                {
                  bg: "bg-emerald-50",
                  border: "border-emerald-200",
                  text: "text-emerald-800",
                  label: "Students like you",
                },
                {
                  bg: "bg-blue-50",
                  border: "border-blue-200",
                  text: "text-blue-800",
                  label: "Why they choose it",
                },
                {
                  bg: "bg-amber-50",
                  border: "border-amber-200",
                  text: "text-amber-800",
                  label: "Counselor note",
                },
              ][variant];

              return (
                <div
                  key={uni.name || `uni-${idx}`}
                  className="group bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-md hover:border-gray-300 transition-all duration-200 hover:-translate-y-0.5"
                >
                  {/* Name + Fit badge */}
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-lg font-bold text-gray-900 pr-3 flex-1 leading-tight">
                      {uni.name || "University"}
                    </h3>
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={`text-xs px-2 py-1 rounded-full font-medium ${
                          confidence === "High"
                            ? "bg-emerald-100 text-emerald-800"
                            : confidence === "Medium"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {confidence}
                      </span>
                      {mismatch && (
                        <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full font-medium">
                          {mismatch}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Shortlist toggle */}
                  {/* <button
                    onClick={() => toggleShortlist(uni.name)}
                    className={`w-10 h-10 rounded-lg border-2 font-medium flex items-center justify-center transition-all text-sm mb-4 ${
                      isSaved
                        ? "bg-emerald-100 text-emerald-700 border-emerald-300 hover:bg-emerald-200"
                        : "bg-gray-50 text-gray-500 border-gray-200 hover:bg-emerald-50 hover:text-emerald-600 hover:border-emerald-300"
                    }`}
                    title={
                      isSaved ? "Remove from shortlist" : "Add to shortlist"
                    }
                  >
                    {isSaved ? "✓" : "+"}
                  </button> */}

                  {/* Insight banner */}
                  <div
                    className={`p-4 rounded-xl mb-4 ${variantStyles.bg} ${variantStyles.border}`}
                  >
                    <p
                      className={`font-semibold text-xs tracking-wide mb-2 ${variantStyles.text}`}
                    >
                      {variantStyles.label}
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed line-clamp-2">
                      {emphasis}
                    </p>
                  </div>

                  {/* Watch-out (desktop only) */}
                  <div className="mb-4 hidden sm:block">
                    <p className="text-xs text-gray-500">
                      Common reason students skip: {ins.watchOut}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t border-gray-100">
                    {uni.web_pages?.[0] && (
                      <a
                        href={uni.web_pages[0]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 text-center py-2.5 px-3 text-xs text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                      >
                        Website →
                      </a>
                    )}
                    {/* <Link
                      to="/eligibility"
                      state={{ university: uni.name, country: selectedCountry }}
                      className="flex-1 text-center py-2.5 px-3 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-all"
                    >
                      Is this realistic for me?
                    </Link> */}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Load more */}
          {visibleCount < intentSorted.length && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisibleCount((v) => v + 10)}
                className="px-8 py-3 border-2 border-gray-300 rounded-xl font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 hover:shadow-sm transition-all"
              >
                Show more universities
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── SHORTLIST CTA ── */}
      {shortlisted.size > 0 && (
        <div className="max-w-4xl mx-auto px-4 pb-16">
          <div className="bg-emerald-50 border-2 border-emerald-200 p-10 rounded-3xl text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Review my options
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Ahmedabad counselors match your {shortlisted.size} saved universit
              {shortlisted.size !== 1 ? "ies" : "y"} to verified admission
              pathways.
            </p>
            <Link
              to={`/contact?country=${selectedCountry}&shortlist=${[...shortlisted].join(",")}&intent=university_selection`}
              className="inline-block px-10 py-4 bg-emerald-600 text-white font-semibold rounded-2xl hover:bg-emerald-700 shadow-lg hover:shadow-xl transition-all"
            >
              Review my options →
            </Link>
          </div>
        </div>
      )}

      {/* FOOTER */}
      <div className="border-t border-gray-200 py-12 text-center px-4">
        <p className="text-sm text-gray-600 mb-1">
          {ALL_UNIVERSITIES.length} universities in local dataset • Counselor
          insights
        </p>
        <p className="text-sm text-gray-500">
          Designed for Ahmedabad students planning overseas study • © 2026
        </p>
      </div>
    </div>
  );
};

export default UniversitiesPage;
